import { useEffect } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import Underline from '@tiptap/extension-underline'
import Placeholder from '@tiptap/extension-placeholder'
import { Box } from '@mui/material'

const EMPTY_HTML = ['', '<p></p>', '<p><br></p>', '<p><br/></p>']

function normalizeHtml(html) {
  if (!html || EMPTY_HTML.includes(html)) return ''
  return html
}

function ToolbarButton({ active, disabled, onClick, children, title  }) {
  return (
    <Box
      component="button"
      type="button"
      disabled={disabled}
      onClick={onClick}
      title={title}
      aria-label={title}
      sx={{
        minWidth: 28,
        height: 28,
        px: '6px',
        py: 0,
        border: '1px solid transparent',
        borderRadius: '4px',
        background: active ? 'rgba(25, 118, 210, 0.12)' : 'transparent',
        borderColor: active ? 'rgba(25, 118, 210, 0.35)' : 'transparent',
        fontSize: '0.75rem',
        fontWeight: 600,
        cursor: disabled ? 'default' : 'pointer',
        color: active ? '#1565c0' : 'rgba(0, 0, 0, 0.75)',
        opacity: disabled ? 0.4 : 1,
        '&:hover': disabled
          ? undefined
          : { background: active ? 'rgba(25, 118, 210, 0.12)' : 'rgba(0, 0, 0, 0.06)' },
      }}
    >
      {children}
    </Box>
  )
}

/**
 * TipTap rich-text editor replacing react-quill-new.
 * Accepts existing Quill-saved HTML without a one-time migration.
 */
export default function RichTextEditor({
  value = '',
  onChange,
  placeholder = '',
  className = '',
  style,
  variant = 'default',
}) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2] },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
      }),
      Image.configure({
        allowBase64: false,
      }),
      Placeholder.configure({
        placeholder,
      }),
    ],
    content: value || '',
    editorProps: {
      attributes: {},
    },
    onUpdate: ({ editor: ed  }) => {
      const html = normalizeHtml(ed.getHTML())
      onChange?.(html)
    },
  })

  useEffect(() => {
    if (!editor) return
    const next = value || ''
    const current = normalizeHtml(editor.getHTML())
    if (normalizeHtml(next) !== current) {
      editor.commands.setContent(next, { emitUpdate: false  })
    }
  }, [editor, value])

  if (!editor) return null

  const setLink = () => {
    const previous = editor.getAttributes('link').href
    const url = window.prompt('URL', previous || 'https://')
    if (url === null) return
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      return
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url  }).run()
  }

  const addImage = () => {
    const url = window.prompt('Image URL', 'https://')
    if (!url) return
    editor.chain().focus().setImage({ src: url  }).run()
  }

  return (
    <Box
      className={className}
      style={style}
      sx={{
        border: '1px solid rgba(0, 0, 0, 0.23)',
        borderRadius: '4px',
        background: 'white',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        '& .ProseMirror': {
          minHeight: variant === 'workflow' ? 60 : variant === 'tall' ? 320 : 120,
          maxHeight: variant === 'workflow' ? 300 : 'none',
          overflowY: variant === 'workflow' ? 'auto' : 'visible',
          p: variant === 'workflow' ? '8px 12px' : '12px',
          outline: 'none',
          fontSize: '0.875rem',
          lineHeight: 1.5,
          '& p': { m: '0 0 0.5em' },
          '& p.is-editor-empty:first-child::before': {
            content: 'attr(data-placeholder)',
            float: 'left',
            color: 'rgba(0, 0, 0, 0.4)',
            pointerEvents: 'none',
            height: 0,
          },
          '& img': { maxWidth: '100%', height: 'auto' },
          '& a': { color: '#1565c0', textDecoration: 'underline' },
        },
      }}
    >
      <Box
        role="toolbar"
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '4px',
          p: '8px',
          borderBottom: '1px solid rgba(0, 0, 0, 0.23)',
          background: '#fafafa',
        }}
      >
        <ToolbarButton
          title="Bold"
          active={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          B
        </ToolbarButton>
        <ToolbarButton
          title="Italic"
          active={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          I
        </ToolbarButton>
        <ToolbarButton
          title="Underline"
          active={editor.isActive('underline')}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        >
          U
        </ToolbarButton>
        {variant === 'default' && (
          <ToolbarButton
            title="Strike"
            active={editor.isActive('strike')}
            onClick={() => editor.chain().focus().toggleStrike().run()}
          >
            S
          </ToolbarButton>
        )}
        <ToolbarButton
          title="Heading 1"
          active={editor.isActive('heading', { level: 1 })}
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 1  }).run()
          }
        >
          H1
        </ToolbarButton>
        <ToolbarButton
          title="Heading 2"
          active={editor.isActive('heading', { level: 2 })}
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 2  }).run()
          }
        >
          H2
        </ToolbarButton>
        <ToolbarButton
          title="Bullet list"
          active={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          •
        </ToolbarButton>
        <ToolbarButton
          title="Ordered list"
          active={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          1.
        </ToolbarButton>
        <ToolbarButton
          title="Link"
          active={editor.isActive('link')}
          onClick={setLink}
        >
          Link
        </ToolbarButton>
        <ToolbarButton title="Image" onClick={addImage}>
          Img
        </ToolbarButton>
        {variant === 'default' && (
          <ToolbarButton
            title="Blockquote"
            active={editor.isActive('blockquote')}
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
          >
            “
          </ToolbarButton>
        )}
        <ToolbarButton
          title="Clear formatting"
          onClick={() =>
            editor.chain().focus().unsetAllMarks().clearNodes().run()
          }
        >
          Clear
        </ToolbarButton>
      </Box>
      <EditorContent editor={editor} />
    </Box>
  )
}
